import { useEffect, useState } from 'react'
import { client } from '@common/config/api/client'
import { CheckItem, Operator } from '@features/subject/subject.types'

export type ContainerAdherenceCheckApiItem = {
  rule_id?: string | null
  tag_id: string
  tag_name: string
  rule: string
  rule_label: string
  target_value: string
  actual_value: string
  status: string
  series_number: string
}

type SiteParams = {
  mode: 'site'
  studyId?: string
  subjectId?: string
  timepointSubmissionId?: string
  containerSubmissionId?: string
}

type QcParams = {
  mode: 'qc'
  studyId?: string
  qcTaskId?: string
  timepointSubmissionId?: string
  containerSubmissionId?: string
}

export type ContainerAdherenceChecksParams = SiteParams | QcParams

function mapToCheckItems(items: ContainerAdherenceCheckApiItem[]): CheckItem[] {
  return items.map((item) => ({
    ruleId: item.rule_id ?? undefined,
    tagId: item.tag_id,
    tagName: item.tag_name,
    rule: item.rule as Operator,
    ruleLabel: item.rule_label,
    targetValue: item.target_value,
    actualValue: item.actual_value,
    status: item.status === 'passed' ? 'passed' : 'failed',
  }))
}

/**
 * Loads persisted criteria check results for a container when `enabled` is true.
 * Supports site (Timepoint) and QC task endpoints.
 */
export const useGetContainerAdherenceChecks = (params: ContainerAdherenceChecksParams, enabled: boolean) => {
  const [checks, setChecks] = useState<CheckItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const siteReady =
    params.mode === 'site' &&
    !!params.studyId &&
    !!params.subjectId &&
    !!params.timepointSubmissionId &&
    !!params.containerSubmissionId

  const qcReady =
    params.mode === 'qc' &&
    !!params.studyId &&
    !!params.qcTaskId &&
    !!params.timepointSubmissionId &&
    !!params.containerSubmissionId

  useEffect(() => {
    const canFetch = enabled && (siteReady || qcReady)

    if (!canFetch) {
      return
    }

    let isCurrentRequest = true
    setIsLoading(true)
    setIsError(false)

    const request =
      params.mode === 'site'
        ? client.get<ContainerAdherenceCheckApiItem[], unknown, true>({
            url:
              '/studies/{study_id}/subjects/{subject_id}/timepoint_submission/{timepoint_submission_id}' +
              '/container_submission/{container_submission_id}/adherence_checks',
            path: {
              study_id: params.studyId!,
              subject_id: params.subjectId!,
              timepoint_submission_id: params.timepointSubmissionId!,
              container_submission_id: params.containerSubmissionId!,
            },
            throwOnError: true,
          })
        : client.get<ContainerAdherenceCheckApiItem[], unknown, true>({
            url: '/qc_tasks/{qc_task_id}/adherence_checks',
            path: {
              qc_task_id: params.qcTaskId!,
            },
            query: {
              study_id: params.studyId!,
              timepoint_submission_id: params.timepointSubmissionId!,
              timepoint_container_submission_id: params.containerSubmissionId!,
            },
            throwOnError: true,
          })

    request
      .then((response) => {
        if (isCurrentRequest) {
          setChecks(mapToCheckItems((response.data as ContainerAdherenceCheckApiItem[]) || []))
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setIsError(true)
          setChecks([])
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false)
        }
      })

    return () => {
      isCurrentRequest = false
    }
    // Intentionally depend on primitive fields rather than the params object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    params.mode,
    params.studyId,
    params.mode === 'site' ? params.subjectId : undefined,
    params.mode === 'qc' ? params.qcTaskId : undefined,
    params.timepointSubmissionId,
    params.containerSubmissionId,
    siteReady,
    qcReady,
  ])

  return { checks, isLoading, isError }
}
